import { Box } from 'designSystem/components/Box'
import { useIsEmbeddedMobileLayout } from 'features/embeddedSidebar/hooks/useIsEmbeddedMobileLayout'
import { SensorPreviewPanel } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/sensors/SensorPreviewPanel'
import { SensorResultsSection } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/sensors/SensorResultsSection'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { useDocChatHeaderState } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatHeaderState'
import { useDocumentResultsKeyboardNavigation } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsKeyboardNavigation'
import { useSensorResultsInteractions } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useSensorResultsInteractions'
import { PageContainer } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/advancedSearchStyles'
import { isDocChatSearchResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { isAiDocumentSearchEngine } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import React from 'react'

import { DocumentResultsSection } from './DocumentResultsSection'
import { UnifiedSearchHeader } from './UnifiedSearchHeader'

function ResultsLayoutBox(props: React.ComponentProps<typeof Box>) {
  return (
    <Box
      display='flex'
      direction='column'
      flex='one'
      minHeight='none'
      overflow='hidden'
      {...props}
    />
  )
}

export const UnifiedSearchResults: React.FC = () => {
  const { onOpenChatbot, onOpenSensorDashboard } = useSearchActions()
  const {
    isLoadingDocuments,
    sensorResults,
    sensorTotalCount,
    isLoadingSensors,
    rawSearchData,
    searchEngine,
  } = useDocumentSearchData()

  const {
    siteId,
    tabId,
    searchState,
    searchInput,
    handleSearchChange,
    handleSearchKeyDown,
    handleSearchSubmit,
    contentFilter,
    searchMode,
    viewMode,
    handleViewModeChange,
    isLinkedDocsMode,
    isActive,
    searchSuggestions,
    onSuggestionSelect,
    onClearSearchHistory,
    favoriteSearches,
    isSearchFavorited,
    toggleSearchFavorite,
    onSearchStateChange,
  } = useAdvancedSearchUX()

  const isDocChatExperience =
    isAiDocumentSearchEngine(searchEngine) && contentFilter === 'documents'
  const docChatSearchResult = isDocChatSearchResult(rawSearchData)
    ? rawSearchData
    : undefined
  const docChatConversation =
    searchState.docChat?.siteId === siteId
      ? searchState.docChat.conversation
      : undefined
  const {
    turns: docChatTurns,
    canSubmit: canSubmitDocChat,
    isGenerating: isDocChatGenerating,
    onDocChatSubmit,
    onDocChatKeyDown,
    onDocChatStop,
  } = useDocChatHeaderState({
    conversation: docChatConversation,
    result: docChatSearchResult,
    isLoadingDocuments,
    query: searchState.query,
    siteId,
    tabId,
    searchMode,
    onSearchSubmit: handleSearchSubmit,
    onSearchKeyDown: handleSearchKeyDown,
    onSearchStateChange,
  })

  const {
    selectedSensor,
    handleSensorClick,
    handleSensorDoubleClick,
    handleCloseSensorPreview,
    handleSensorAddToContext,
  } = useSensorResultsInteractions({
    onOpenChatbot,
    onOpenSensorDashboard,
    onSearchStateChange,
  })

  useDocumentResultsKeyboardNavigation({
    enabled: isActive && contentFilter === 'sensors',
    handleSensorClick,
    handleSensorDoubleClick,
    mode: 'sensors',
    selectedSensor,
    sensorResults,
  })

  const stackSensorPreview = useIsEmbeddedMobileLayout()
  const sensorPreview =
    contentFilter === 'sensors' && viewMode === 'table' && selectedSensor ? (
      <SensorPreviewPanel
        sensor={selectedSensor}
        isOpen
        placement={stackSensorPreview ? 'top' : 'side'}
        onClose={handleCloseSensorPreview}
        onOpenDashboard={handleSensorDoubleClick}
        onOpenNewTab={onOpenSensorDashboard}
        onAddToContext={handleSensorAddToContext}
      />
    ) : null

  return (
    <PageContainer $stacked={stackSensorPreview}>
      {stackSensorPreview ? sensorPreview : null}
      <ResultsLayoutBox>
        {contentFilter === 'documents' ? (
          <DocumentResultsSection
            isLinkedDocsMode={isLinkedDocsMode}
            isDocChatExperience={isDocChatExperience}
            contentFilter={contentFilter}
            searchInput={searchInput}
            onSearchChange={handleSearchChange}
            onSearchKeyDown={handleSearchKeyDown}
            onSearchSubmit={handleSearchSubmit}
            viewMode={viewMode}
            suggestions={searchSuggestions}
            onSuggestionSelect={onSuggestionSelect}
            onClearHistory={onClearSearchHistory}
            favoriteSearches={favoriteSearches}
            isSearchFavorited={isSearchFavorited}
            onToggleSearchFavorite={toggleSearchFavorite}
            docChatTurns={docChatTurns}
            onDocChatKeyDown={onDocChatKeyDown}
            onDocChatSubmit={onDocChatSubmit}
            canSubmitDocChat={canSubmitDocChat}
            isDocChatGenerating={isDocChatGenerating}
            onDocChatStop={onDocChatStop}
          />
        ) : (
          <UnifiedSearchHeader
            isLinkedDocsMode={isLinkedDocsMode}
            isDocChatExperience={isDocChatExperience}
            contentFilter={contentFilter}
            searchInput={searchInput}
            onSearchChange={handleSearchChange}
            onSearchKeyDown={handleSearchKeyDown}
            onSearchSubmit={handleSearchSubmit}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            suggestions={searchSuggestions}
            onSuggestionSelect={onSuggestionSelect}
            onClearHistory={onClearSearchHistory}
            favoriteSearches={favoriteSearches}
            isSearchFavorited={isSearchFavorited}
            onToggleSearchFavorite={toggleSearchFavorite}
            docChatTurns={docChatTurns}
            onDocChatKeyDown={onDocChatKeyDown}
            onDocChatSubmit={onDocChatSubmit}
            canSubmitDocChat={canSubmitDocChat}
            isDocChatGenerating={isDocChatGenerating}
            onDocChatStop={onDocChatStop}
          />
        )}

        {contentFilter === 'sensors' && (
          <ResultsLayoutBox>
            <SensorResultsSection
              sensors={sensorResults}
              totalCount={sensorTotalCount}
              isLoading={isLoadingSensors}
              onSensorClick={handleSensorClick}
              onSensorDoubleClick={handleSensorDoubleClick}
              selectedSensorId={selectedSensor?.id}
              fullPage
            />
          </ResultsLayoutBox>
        )}
      </ResultsLayoutBox>
      {stackSensorPreview ? null : sensorPreview}
    </PageContainer>
  )
}
